import { Components } from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";

import type {} from "@mui/x-date-pickers/themeAugmentation";
import { palette } from "./palette";
import { shadows } from "./shadows";
import { shape } from "./shape";
import { typography } from "./typography";

export const components: Components = {
  MuiCssBaseline: {
    styleOverrides: {
      "& :root": {
        "--focus-border": palette.secondary.main,
        "--separator-border": palette.divider,
      },
      "& .split-view-view-visible": {
        "&:before": {
          width: "2px !important",
        },
      },

      "& #root": {
        height: "100vh",
        width: "100vw",
      },
      "& #nprogress": {
        "& .bar": {
          background: `${palette.primary.main} !important`,
        },
        "& .peg": {
          boxShadow: `0 0 10px ${palette.primary.main}, 0 0 5px ${palette.primary.main} !important`,
        },
      },
      "& .ql-toolbar": {
        fontFamily: "Inter, sans-serif",
        borderTopLeftRadius: shape.borderRadius,
        borderTopRightRadius: shape.borderRadius,
        borderColor: `${palette.divider} !important`,
      },
      "& .ql-container": {
        fontFamily: "Inter, sans-serif",
        fontSize: typography.body1?.fontSize,
        borderBottomLeftRadius: shape.borderRadius,
        borderBottomRightRadius: shape.borderRadius,
        borderColor: `${palette.divider} !important`,
      },
      "& .ql-editor": {
        minHeight: 100,
      },
      "& .SnackbarItem-variantInfo": {
        backgroundColor: `${palette.info.main} !important`,
      },
      "& .SnackbarItem-variantSuccess": {
        backgroundColor: `${palette.success.main} !important`,
      },
      "& .SnackbarItem-variantWarning": {
        backgroundColor: `${palette.warning.main} !important`,
      },
      "& .SnackbarItem-variantError": {
        backgroundColor: `${palette.error.main} !important`,
      },
      "& .announcekit-widget-badge": {
        position: "absolute !important",
        bottom: 14,
        right: -2,
      },
    },
  },
  MuiTypography: {
    defaultProps: {
      variantMapping: {
        h1: "h1",
        h2: "h2",
        h3: "h3",
        h4: "h4",
        h5: "h6",
        h6: "h6",
        subtitle1: "h2",
        subtitle2: "h2",
        body1: "div",
        body2: "div",
        overline: "div",
        caption: "div",
        button: "div",
      },
    },
  },
  MuiAvatarGroup: {
    styleOverrides: {
      root: {
        width: "fit-content",
      },
    },
  },
  MuiAvatar: {
    defaultProps: {
      variant: "rounded",
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        textTransform: "none",
      },
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        textTransform: "none",
      },
      sizeSmall: {
        padding: "6px 16px",
      },
      sizeMedium: {
        padding: "8px 20px",
      },
      sizeLarge: {
        padding: "11px 24px",
      },
      textSizeSmall: {
        padding: "7px 12px",
      },
      textSizeMedium: {
        padding: "9px 16px",
      },
      textSizeLarge: {
        padding: "12px 16px",
      },
    },
  },
  MuiMenu: {
    defaultProps: {
      slotProps: {
        paper: {
          elevation: 16,
        },
      },
    },
    styleOverrides: {
      paper: {
        border: `1px solid ${palette.divider}`,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        display: "flex",
        flexDirection: "column",
        padding: "16px",
        boxShadow: "none",
      },
    },
  },
  MuiCardHeader: {
    defaultProps: {
      titleTypographyProps: { variant: "subtitle1" },
      subheaderTypographyProps: { variant: "body2" },
    },
    styleOverrides: {
      root: {
        padding: "24px",
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: "24px",
      },
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: "16px 24px",
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: "24px",
        border: "none",
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      flexContainer: {
        height: "100%",
      },
      vertical: {
        "& .MuiTab-root": {
          borderRadius: shape.borderRadius,
          minWidth: "100%",
          justifyContent: "flex-start",

          "&.Mui-selected": {
            backgroundColor: lighten(palette.primary.main, 0.95),
          },
        },

        "& .MuiTabs-indicator": {
          display: "none",
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: "none",
        display: "flex",
        alignItems: "center",
        minHeight: "auto",
        textAlign: "left",

        "& svg": {
          marginRight: 10,
          height: 20,
          width: 20,
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: "none",
        padding: "10px 24px",
      },
      head: {
        color: palette.table.head.text,
        backgroundColor: palette.table.head.background,
        fontSize: "12px",
        fontWeight: 600,
        lineHeight: 1,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        borderBottom: "none",
      },
      footer: {
        borderBottom: "none",
      },
      sizeMedium: {
        padding: "10px 16px",
        "&.MuiTableCell-head": {
          height: 42,
        },
      },
      sizeSmall: {
        padding: "6px 16px",
        "&.MuiTableCell-head": {
          height: 36,
        },
      },
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      root: {
        borderTop: `1px solid ${palette.divider}`,
      },
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      paper: {
        border: `1px solid ${palette.divider}`,
        boxShadow: shadows[20],
        marginTop: 6,
        marginBottom: 6,
      },
      option: {
        position: "relative",
        "&.Mui-focused": {
          "&:before": {
            content: "''",
            position: "absolute",
            background: palette.primary.main,
            borderTopRightRadius: "50%",
            borderBottomRightRadius: "50%",
            height: "100%",
            top: 0,
            left: 0,
            width: 2,
          },
        },
      },
    },
  },
  MuiListItemText: {
    defaultProps: {
      primaryTypographyProps: {
        variant: "body2",
        fontSize: "0.875rem",
        fontWeight: 600,
      },
      secondaryTypographyProps: {
        variant: "caption",
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: 32,
      },
    },
  },
  MuiInputBase: {
    defaultProps: {
      size: "small",
    },
  },
  MuiFormControl: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      root: {
        width: "100%",
      },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: {
        transition: `
             background-color ${300}ms cubic-bezier(0.4, 0, 0.2, 1),
             box-shadow ${300}ms cubic-bezier(0.4, 0, 0.2, 1)`,
        "&:before": {
          transition: `
             border-color ${300}ms cubic-bezier(0.4, 0, 0.2, 1)`,
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        "*": {
          transition: `
             border-color ${300}ms cubic-bezier(0.4, 0, 0.2, 1),
             box-shadow ${300}ms cubic-bezier(0.4, 0, 0.2, 1)`,
        },

        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: `${palette.divider}`,
        },

        "&.Mui-focused": {
          "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1,
            boxShadow: `0 0 0 2px ${alpha(palette.primary.main, 0.2)}`,
            borderColor: `${palette.primary.main} !important`,
          },

          "&.Mui-error": {
            "& .MuiOutlinedInput-notchedOutline": {
              boxShadow: `0 0 0 2px ${alpha(palette.error.main, 0.2)}`,
              borderColor: `${palette.error.main} !important`,
            },
          },
        },
        "&:hover": {
          "&:not(.Mui-disabled)": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: `${palette.primary.main}`,
            },

            "&.Mui-error": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: `${palette.error.main}`,
              },
            },
          },
        },
      },
      notchedOutline: {
        "& > legend": {
          backgroundColor: "#FFFFFf",
          visibility: "initial",
          "& > span": {
            visibility: "hidden",
          },
        },
      },
    },
  },
};
